import React from "react";
import { useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import {
  searchItemsAPI,
  createConversationAdminAPI,
  searchUsersAPI,
} from "../reduxData/user/userAction";

const SearchSelect = ({
  label,
  placeholder,
  onSelect,
  selectedItem,
  onRemove,
  searchFunction,
  displayField = "user_name",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term.length < 2) {
        setResults([]);
        setIsLoading(false);
        return;
      }
      try {
        const data = await searchFunction(term);
        setResults(data);
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [searchFunction]
  );

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsLoading(true);
    debouncedSearch(term);
  };

  const handleSelect = (item) => {
    onSelect(item);
    setSearchTerm("");
    setResults([]);
  };

  if (selectedItem) {
    return (
      <div className="mb-3">
        <label className="form-label">{label}</label>
        <div className="d-flex align-items-center p-2 border rounded">
          {selectedItem.photos && (
            <img
              src={selectedItem.photos[0] || "https://placehold.co/40x40"}
              alt={selectedItem[displayField]}
              className="rounded me-2"
              style={{ width: "30px", height: "30px", objectFit: "cover" }}
            />
          )}
          <span>{selectedItem[displayField]}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            aria-label="Remove"
            onClick={onRemove}
          ></button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
      />
      {isLoading && (
        <small className="form-text text-muted">Searching...</small>
      )}
      {results.length > 0 && (
        <ul className="list-group mt-1">
          {results.map((item) => (
            <li
              key={item._id}
              className="list-group-item list-group-item-action"
              style={{ cursor: "pointer" }}
              onClick={() => handleSelect(item)}
            >
              <div className="d-flex align-items-center">
                {item.photos && (
                  <img
                    src={item.photos[0] || "https://placehold.co/40x40"}
                    alt={item[displayField]}
                    className="rounded me-3"
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div className="flex-grow-1">
                  {item[displayField]}
                  {item.email ? ` (${item.email})` : ""}
                  {item.relisted_from && (
                    <span className="badge bg-danger ms-2">Relist</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CreateConversationModal = ({ show, onHide, onSuccess }) => {
  const [participantOne, setParticipantOne] = useState(null);
  const [participantTwo, setParticipantTwo] = useState(null);
  const [relatedItem, setRelatedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setParticipantOne(null);
    setParticipantTwo(null);
    setRelatedItem(null);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetState();
    onHide();
  };

  const handleSubmit = async () => {
    if (!participantOne || !participantTwo) {
      return toast.error("Please select two participants.");
    }
    if (participantOne._id === participantTwo._id) {
      return toast.error("Participants cannot be the same user.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        participantOne: participantOne.user_name,
        participantTwo: participantTwo.user_name,
        relatedItemId: relatedItem?._id || null,
      };
      const newConversation = await createConversationAdminAPI(payload);
      if (newConversation) {
        onSuccess();
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Conversation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SearchSelect
          label="Participant 1"
          placeholder="Search for the first user..."
          selectedItem={participantOne}
          onSelect={setParticipantOne}
          onRemove={() => setParticipantOne(null)}
          searchFunction={searchUsersAPI}
          displayField="user_name"
        />

        <SearchSelect
          label="Participant 2"
          placeholder="Search for the second user..."
          selectedItem={participantTwo}
          onSelect={setParticipantTwo}
          onRemove={() => setParticipantTwo(null)}
          searchFunction={searchUsersAPI}
          displayField="user_name"
        />

        <hr />

        <SearchSelect
          label="Related Item (Optional)"
          placeholder="Search for an item..."
          selectedItem={relatedItem}
          onSelect={setRelatedItem}
          onRemove={() => setRelatedItem(null)}
          searchFunction={searchItemsAPI}
          displayField="item_name"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Conversation"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateConversationModal;
